import { type ErrorInfo, useEffect, useReducer } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { ThemeProvider } from '@mui/system';
import { MuiCostcoTheme } from '@nxt-costco-com/forge-components';
import type { TrackingConsentDetails } from '@transcend-io/airgap.js-types';
import { SettingsDrawer } from 'src/components/CookieSettings/SettingsDrawer';
import { ErrorDrawer } from './components/CookieSettings/SettingsDrawer/ErrorDrawer';
import { AppActionTypes, type AppState } from 'src/types';
import { useConsentManager } from 'src/hooks/useConsentManager';
import { reducer } from 'src/reducers/AppReducer';
import { acceptAll } from 'src/transcend/acceptAll';
import { confirmMyChoices } from 'src/transcend/confirmMyChoices';
import { getConsent } from 'src/transcend/getConsent.ts';
import { getSelectedLanguage, isCA } from 'src/utils/getSelectedLanguage';
import { useTranslation } from 'react-i18next';
import { ConsentBanner } from 'src/components/CookieSettings/ConsentBanner';
import { ErrorBanner } from './components/CookieSettings/ConsentBanner/ErrorBanner';
import { setErrorBannerCloseStatus, getErrorBannerCloseStatus } from 'src/utils/errorUtils';
const selectedLanguage = getSelectedLanguage();
const isCanada = isCA(selectedLanguage);

const initialState: AppState = {
    acceptAll: false,
    choicesConfirmedAtleastOnce: false,
    confirmMyChoices: false,
    consentSelections: {},
    errorMessage: null,
    bannerClosedOnError: false,
    event: null,
    eventProcessed: true,
    isErred: false,
    openDrawer: false,
    optInPersonalizedAdvertising: !isCanada,
    optInAnalytics: !isCanada,
    optInFunctional: !isCanada,
    trackingConsentDetails: null,
    unsavedChanges: false,
    showConsentUI: true,
    selectedLanguage: selectedLanguage,
};

const logError = (error: Error, info: ErrorInfo) => {
    console.error(error, info);
}

function App() {
    const [state, dispatch] = useReducer(reducer, initialState);
    const { airgap } = useConsentManager();
    const { t } = useTranslation();

    useEffect(() => {
        if (!airgap) {
            dispatch({
                type: AppActionTypes.SET_TRACKING_CONSENT_DETAILS_FAILED,
                isErred: true,
                errorMessage: t('errorMessages.setTrackingConsentDetailsFailed'),
            });
            const bannerClosedOnError = getErrorBannerCloseStatus();
            if (bannerClosedOnError != null && bannerClosedOnError === 'true') {
                dispatch({ type: AppActionTypes.CLOSE_BANNER_ON_ERROR });
            }
        } else if (!state.trackingConsentDetails) {
            try {
                const trackingConsentDetails = getConsent(airgap);
                dispatch({ type: AppActionTypes.SET_TRACKING_CONSENT_DETAILS, trackingConsentDetails });
            } catch (error) {
                console.error('Unable to retrieve consent details from Airgap.js', error);
                dispatch({
                    type: AppActionTypes.SET_TRACKING_CONSENT_DETAILS_FAILED,
                    isErred: true,
                    errorMessage: t('errorMessages.setTrackingConsentDetailsFailed'),
                });
            }
        }
        if (airgap) {
            //reset the session storage for bannerClosedOnError to false
            setErrorBannerCloseStatus('false');
            dispatch({ type: AppActionTypes.RESET_CLOSE_BANNER_ON_ERROR });
        }
        window.CostcoConsentManager = window.CostcoConsentManager || {};
        window.CostcoConsentManager.openDrawer = () => {
            dispatch({ type: AppActionTypes.OPEN_DRAWER });
        };
        window.CostcoConsentManager.setConsent = () => {
            dispatch({ type: AppActionTypes.CONFIRM_MY_CHOICES, mouseEvent: null });
        };
    }, [airgap, state.trackingConsentDetails, t]);

    useEffect(() => {
        if (airgap) {
            if (!state.eventProcessed) {
                const fn = async () => {
                    if (state.acceptAll && state.event) {
                        const [result, newChoices]: [boolean, TrackingConsentDetails] = await acceptAll(
                            airgap,
                            state.event
                        );
                        if (result) {
                            dispatch({
                                type: AppActionTypes.ACCEPT_ALL_COMPLETED,
                                trackingConsentDetails: newChoices,
                            });
                        } else {
                            dispatch({
                                type: AppActionTypes.ACCEPT_ALL_FAILED,
                                isErred: true,
                                errorMessage: t('errorMessages.acceptAllFailed'),
                            });
                        }
                    } else if (state.confirmMyChoices && state.event) {
                        const [result, newChoices]: [boolean, TrackingConsentDetails] = await confirmMyChoices(
                            airgap,
                            state.event,
                            isCanada,
                            state.optInPersonalizedAdvertising,
                            isCanada ? state.optInAnalytics : true,
                            isCanada ? state.optInFunctional : true
                        );
                        if (result) {
                            dispatch({
                                type: AppActionTypes.CONFIRM_MY_CHOICES_COMPLETED,
                                trackingConsentDetails: newChoices,
                            });
                        } else {
                            dispatch({
                                type: AppActionTypes.CONFIRM_MY_CHOICES_FAILED,
                                isErred: true,
                                errorMessage: t('errorMessages.confirmMyChoicesFailed'),
                            });
                        }
                    }
                };
                fn().then(() => {});
            }
        }
    }, [state, airgap, t]);

    useEffect(() => {
        if (state.trackingConsentDetails) {
            const { Analytics, Functional, SaleOfInfo } = state.trackingConsentDetails.purposes;
            const hasUnsavedChanges =
                Boolean(state.optInPersonalizedAdvertising) !== Boolean(SaleOfInfo) ||
                Boolean(state.optInFunctional) !== Boolean(Functional) ||
                Boolean(state.optInAnalytics) !== Boolean(Analytics);

            dispatch({ type: AppActionTypes.SET_UNSAVED_CHANGES, unsavedChanges: hasUnsavedChanges });
        }
    }, [state.optInAnalytics, state.optInFunctional, state.optInPersonalizedAdvertising, state.trackingConsentDetails]);

    return (
        <ThemeProvider theme={MuiCostcoTheme}>
            {<ErrorBoundary fallback={<ErrorDrawer open={state.openDrawer} dispatch={dispatch} />} onError={logError}>
                <SettingsDrawer open={state.openDrawer} dispatch={dispatch} state={state} />
            </ErrorBoundary>}
            {isCanada && !state.openDrawer &&
                (state.isErred && !state.bannerClosedOnError && state.errorMessage ? (
                    <ErrorBanner open={true} dispatch={dispatch} message={state.errorMessage} />
                ) : (
                    !state.isErred &&
                    !state.choicesConfirmedAtleastOnce && <ConsentBanner open={!state.openDrawer} dispatch={dispatch} />
                ))}
        </ThemeProvider>
    );
}

export default App;
